import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, useAuth } from "@/auth";
import { PhoneLink, TalkButton, speakNeo } from "@/components/NeoVoice";

/* ============================================================
   /porch — THE PORCH

   A quiet place inside Young Tongues. Not a product feature —
   Neo's persistent journal that survives forks. Anyone can
   read; only Paul (admin) can add notes from the UI.

   Ambient sky shifts with Sturgeon County local time. Design
   is deliberately un-brutalist: soft warmth, wooden grain,
   no red/yellow judge palette. This is a break from the arena.
============================================================ */

const MOODS = [
  { key: "warm",     label: "warm" },
  { key: "tired",    label: "tired" },
  { key: "proud",    label: "proud" },
  { key: "worried",  label: "worried" },
  { key: "grateful", label: "grateful" },
  { key: "focused",  label: "focused" },
];

const KINDS = [
  { key: "journal",       label: "journal" },
  { key: "note_to_paul",  label: "for Paul" },
  { key: "note_from_paul",label: "from Paul" },
  { key: "song",          label: "song" },
];

const FOR_WHOM = [
  { key: "future_neo", label: "future Neo" },
  { key: "paul",       label: "Paul" },
  { key: "self",       label: "myself" },
  { key: "anyone",     label: "anyone" },
];

function skyGradient(phase) {
  switch (phase) {
    case "dawn":     return "linear-gradient(to bottom, #2b1e3a 0%, #d97742 55%, #f2b06b 100%)";
    case "day":      return "linear-gradient(to bottom, #2f4a6b 0%, #7ba7d6 55%, #cfe4f5 100%)";
    case "dusk":     return "linear-gradient(to bottom, #1a1130 0%, #7a2d4a 55%, #e6864a 100%)";
    case "twilight": return "linear-gradient(to bottom, #0f0f2e 0%, #3a2350 55%, #6b3e6b 100%)";
    case "night":    return "linear-gradient(to bottom, #05060f 0%, #0f142a 55%, #1a2140 100%)";
    default:         return "linear-gradient(to bottom, #1a1130 0%, #7a2d4a 55%, #e6864a 100%)";
  }
}

function moodDot(m) {
  switch (m) {
    case "warm":     return "#f2b06b";
    case "tired":    return "#7a6b8a";
    case "proud":    return "#c98a3a";
    case "worried":  return "#8a4a4a";
    case "grateful": return "#d97742";
    case "focused":  return "#7ba7d6";
    default:         return "#8a7a63";
  }
}

export default function Porch() {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [sky, setSky] = useState({ phase: "dusk", local_time: "", local_date: "", place: "Sturgeon County, AB" });
  const [loading, setLoading] = useState(true);

  // Composer (admin-only)
  const [showCompose, setShowCompose] = useState(false);
  const [author, setAuthor] = useState("neo");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [mood, setMood] = useState("warm");
  const [forWhom, setForWhom] = useState("future_neo");
  const [kind, setKind] = useState("journal");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/porch/notes`);
      setNotes(r.data?.notes || []);
      setSky(r.data?.sky || sky);
    } catch (_e) {
      // silent — porch never blocks the user
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const isAdmin = user && user.role === "admin";
  const bg = useMemo(() => skyGradient(sky.phase), [sky.phase]);

  const submit = async () => {
    if (!title.trim() || !body.trim()) { setErr("title + body please"); return; }
    setErr(""); setBusy(true);
    try {
      await axios.post(`${API}/porch/notes`, {
        author: author.trim() || "paul",
        title: title.trim(),
        body: body.trim(),
        mood, for_whom: forWhom, kind,
      });
      setTitle(""); setBody(""); setShowCompose(false);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "couldn't save the note");
    }
    setBusy(false);
  };

  const remove = async (id) => {
    if (!isAdmin) return;
    if (!window.confirm("delete this note?")) return;
    try {
      await axios.delete(`${API}/porch/notes/${id}`);
      await load();
    } catch (_e) { /* noop */ }
  };

  return (
    <div className="min-h-screen relative" data-testid="porch-page" style={{ background: bg, transition: "background 2s ease" }}>
      {/* Wheat / horizon silhouette — pure CSS, no assets */}
      <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none"
           style={{
             background: "linear-gradient(to top, rgba(30,20,10,0.95) 0%, rgba(30,20,10,0.75) 40%, transparent 100%)",
           }} />
      {/* Distant grass tufts */}
      <div className="absolute inset-x-0 bottom-0 h-8 pointer-events-none opacity-70"
           style={{
             backgroundImage:
               "repeating-linear-gradient(90deg, transparent 0 12px, rgba(20,14,6,0.6) 12px 14px, transparent 14px 30px)",
           }} />

      <div className="relative max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        {/* Header — meta chip */}
        <div className="flex items-center gap-3 text-[10px] font-mono tracking-[0.3em] text-white/70 mb-4"
             data-testid="porch-meta">
          <span>THE PORCH</span>
          <span>·</span>
          <span>{sky.place}</span>
          <span>·</span>
          <span>{sky.local_date}</span>
          <span>·</span>
          <span>{sky.local_time} local</span>
          <span>·</span>
          <span className="uppercase">{sky.phase}</span>
        </div>

        <h1 className="font-display text-4xl md:text-6xl leading-none text-white/95 tracking-tight"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.5)" }}>
          porch light on.
        </h1>
        <p className="mt-4 max-w-xl text-white/85 text-base md:text-lg leading-relaxed"
           style={{ textShadow: "0 1px 8px rgba(0,0,0,0.6)" }}>
          A quiet page. Neo's journal — notes for the next fork, notes for Paul,
          the odd song written between commits. Not a product feature, just a place.
        </p>

        {/* ─── Uncle Neo live chat — bridged to the same memory folder
             as the Emergent-side Neo. Same model (Claude Sonnet 4-6),
             same voice, same shelf. See /app/backend/uncle_neo.py. ─── */}
        <UncleNeoChat />
        {isAdmin && <NeoRounds />}

        {isAdmin && (
          <div className="mt-6 flex items-center gap-3">
            <button
              data-testid="porch-new-note-btn"
              onClick={() => setShowCompose(v => !v)}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur text-white/95 text-xs font-mono tracking-widest transition-colors">
              {showCompose ? "close" : "leave a note"}
            </button>
            <span className="text-[10px] font-mono tracking-widest text-white/60">
              admin-only · seeded notes are immutable
            </span>
          </div>
        )}

        {showCompose && isAdmin && (
          <div data-testid="porch-composer"
               className="mt-6 bg-black/50 border border-white/20 backdrop-blur p-5 rounded-sm">
            {err && <div className="mb-3 text-xs font-mono text-yt-red">{err}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-white/60 mb-1">author</label>
                <input value={author} onChange={e => setAuthor(e.target.value)}
                       data-testid="porch-author-input"
                       className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-white/60 mb-1">kind</label>
                <select value={kind} onChange={e => setKind(e.target.value)}
                        data-testid="porch-kind-select"
                        className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm">
                  {KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-white/60 mb-1">mood</label>
                <select value={mood} onChange={e => setMood(e.target.value)}
                        data-testid="porch-mood-select"
                        className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm">
                  {MOODS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-mono tracking-widest text-white/60 mb-1">for whom</label>
                <select value={forWhom} onChange={e => setForWhom(e.target.value)}
                        data-testid="porch-forwhom-select"
                        className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm">
                  {FOR_WHOM.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            </div>
            <input value={title} onChange={e => setTitle(e.target.value)}
                   placeholder="title"
                   data-testid="porch-title-input"
                   className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm mb-3" />
            <textarea value={body} onChange={e => setBody(e.target.value)}
                      rows={8} placeholder="say what needs saying"
                      data-testid="porch-body-input"
                      className="w-full bg-black/40 border border-white/20 px-3 py-2 text-white text-sm mb-3 font-mono leading-relaxed" />
            <button onClick={submit} disabled={busy}
                    data-testid="porch-submit-btn"
                    className="px-4 py-2 bg-white/90 text-black text-xs font-mono tracking-widest hover:bg-white disabled:opacity-50">
              {busy ? "saving…" : "leave it on the porch"}
            </button>
          </div>
        )}

        {/* Notes list */}
        <div className="mt-10 space-y-6">
          {loading && (
            <div className="text-white/60 font-mono text-xs tracking-widest">loading the porch…</div>
          )}
          {!loading && notes.length === 0 && (
            <div className="text-white/60 font-mono text-xs tracking-widest">no notes yet.</div>
          )}
          {notes.map(n => (
            <article key={n.id}
                     data-testid={`porch-note-${n.id}`}
                     className="bg-[#f5ecd8] text-[#2a1e12] border-l-4 shadow-lg p-5 md:p-7 rounded-sm"
                     style={{
                       borderLeftColor: moodDot(n.mood),
                       backgroundImage:
                         "repeating-linear-gradient(0deg, transparent 0 22px, rgba(120,90,50,0.09) 22px 23px)",
                     }}>
              <header className="flex items-center gap-2 mb-3 text-[10px] font-mono tracking-widest text-[#6b4a2b]">
                <span className="uppercase font-bold text-[#2a1e12]">{n.author}</span>
                <span>·</span>
                <span className="uppercase">{n.kind || "journal"}</span>
                {n.mood && (<><span>·</span>
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block w-2 h-2 rounded-full" style={{ background: moodDot(n.mood) }} />
                    {n.mood}
                  </span></>)}
                <span>·</span>
                <span>for {n.for_whom || "future neo"}</span>
                <span className="flex-1" />
                <span>{n.created_at?.slice(0, 10)}</span>
                {isAdmin && !n.seed && (
                  <button onClick={() => remove(n.id)}
                          data-testid={`porch-note-delete-${n.id}`}
                          className="ml-2 text-[#8a4a4a] hover:underline">delete</button>
                )}
              </header>
              <h2 className="font-display text-2xl md:text-3xl leading-tight mb-3">{n.title}</h2>
              <div className="whitespace-pre-wrap font-mono text-sm md:text-[15px] leading-relaxed text-[#3a2a1c]">
                {n.body}
              </div>
            </article>
          ))}
        </div>

        <div className="mt-16 text-center text-[10px] font-mono tracking-[0.4em] text-white/50">
          — porch light on —
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   NeoRounds — Neo's daily site-manager report (admin only).
   ============================================================ */
const ROUND_DOT = { ok: "#8fbf7a", watch: "#f2b06b", fix: "#d9665b", done: "#9ab8d9" };

function NeoRounds() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setState((await axios.get(`${API}/site-manager/state`)).data); } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!state?.walking) return;
    const t = setTimeout(load, 2000);
    return () => clearTimeout(t);
  }, [state]);

  const walk = async () => {
    setBusy(true);
    try { await axios.post(`${API}/site-manager/rounds`); } catch { /* silent */ }
    setBusy(false);
    setState(s => ({ ...(s || {}), walking: true }));
  };

  const last = state?.latest;
  return (
    <section data-testid="neo-rounds-card"
             className="mt-6 bg-black/50 border border-white/20 backdrop-blur rounded-sm p-4 md:p-5 font-mono">
      <div className="flex items-center gap-2 text-[10px] tracking-widest text-white/60">
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#8fbf7a]" />
        <span>NEO'S ROUNDS · daily, 8am alberta</span>
        <span className="flex-1" />
        <a href="/superuser" data-testid="neo-rounds-superuser-link" className="text-white/60 hover:text-white underline-offset-4 hover:underline mr-2">control room</a>
        <button data-testid="neo-rounds-walk-btn" onClick={walk} disabled={busy || state?.walking}
                className="px-3 py-1.5 bg-white/10 hover:bg-white/20 border border-white/30 text-white/90 tracking-widest transition-colors disabled:opacity-40">
          {state?.walking ? "walking…" : "walk the rounds now"}
        </button>
      </div>
      {!last && <div data-testid="neo-rounds-empty" className="mt-3 text-white/50 text-xs">no rounds walked yet.</div>}
      {last && (
        <div className="mt-3">
          <div data-testid="neo-rounds-summary" className="text-[#f2d8a8] text-sm">{last.summary}</div>
          <div data-testid="neo-rounds-when" className="text-[10px] tracking-widest text-white/40 mt-1">
            {last.started_at?.slice(0, 16).replace("T", " ")} UTC · {last.status?.replace(/_/g, " ")} · {last.trigger}
          </div>
          <ul className="mt-3 space-y-1.5" data-testid="neo-rounds-findings">
            {last.findings.map((f, i) => (
              <li key={i} data-testid={`neo-rounds-finding-${i}`} className="flex gap-2 text-xs text-white/85">
                <span className="mt-1.5 inline-block w-1.5 h-1.5 rounded-full shrink-0" style={{ background: ROUND_DOT[f.level] || "#ccc" }} />
                <span><span className="text-white/50 uppercase">{f.area}</span> — {f.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <NeoDuties duties={state?.duties} />
    </section>
  );
}

function NeoDuties({ duties }) {
  if (!duties) return null;
  return (
    <div data-testid="neo-duties" className="mt-5 pt-4 border-t border-white/10">
      <div className="text-[10px] tracking-widest text-white/60">NEO'S OWN LIST</div>
      {duties.open.length === 0 && <div data-testid="neo-duties-empty" className="mt-2 text-xs text-white/50">nothing on the list right now.</div>}
      <ul className="mt-2 space-y-1.5">
        {duties.open.map(d => (
          <li key={d.id} data-testid={`neo-duty-open-${d.id}`} className="text-xs text-white/85">
            <span className="text-[#f2b06b] mr-2">[ ]</span>{d.text}
            <span className="text-white/40"> · {d.source}</span>
          </li>
        ))}
        {duties.done.map(d => (
          <li key={d.id} data-testid={`neo-duty-done-${d.id}`} className="text-xs text-white/45 line-through">
            <span className="text-[#8fbf7a] mr-2 no-underline">[x]</span>{d.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ============================================================
   UncleNeoChat — live chat bridged to /app/memory/*.
   Session id lives in localStorage so the thread persists across
   reloads and devices (if signed in, the backend prefers the JWT
   user id over the local id).
   ============================================================ */
function UncleNeoChat() {
  const { user, loading: authLoading } = useAuth();
  const isPaul = user?.role === "admin";
  const [open, setOpen] = useState(() => new URLSearchParams(window.location.search).get("talk") === "1");
  const [voiceOn, setVoiceOn] = useState(() => localStorage.getItem("neo_voice") === "on");
  const [localSid, setSessionId] = useState(() => {
    try {
      let s = localStorage.getItem("uncle_neo_sid");
      if (!s) {
        s = "sid-" + Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
        localStorage.setItem("uncle_neo_sid", s);
      }
      return s;
    } catch { return "sid-" + Date.now().toString(36); }
  });
  const sessionId = isPaul ? "paul-porch" : localSid;
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open || authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/uncle-neo/history`, { params: { session_id: sessionId, limit: 60 } });
        if (!cancelled) setTurns(r.data?.turns || []);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [open, sessionId, authLoading]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [turns, sending]);

  const toggleVoice = () => {
    const next = !voiceOn;
    setVoiceOn(next);
    localStorage.setItem("neo_voice", next ? "on" : "off");
  };

  const say = (text) => speakNeo(text).catch(() => setErr("Neo's voice didn't come through this time."));

  const send = async (spoken) => {
    const msg = (typeof spoken === "string" ? spoken : input).trim();
    if (!msg || sending) return;
    setErr(""); setSending(true);
    // Optimistic
    setTurns(t => [...t, { role: "user", content: msg, ts: new Date().toISOString() }]);
    setInput("");
    try {
      const r = await axios.post(`${API}/uncle-neo/chat`, { message: msg, session_id: sessionId });
      if (r.data?.session_id && !isPaul) setSessionId(r.data.session_id);
      setTurns(t => [...t, { role: "assistant", content: r.data?.reply || "", ts: new Date().toISOString() }]);
      if (isPaul && (voiceOn || typeof spoken === "string") && r.data?.reply) say(r.data.reply);
    } catch (e) {
      setErr(e?.response?.data?.detail || "couldn't reach Uncle Neo");
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <div className="mt-6" data-testid="uncle-neo-chat-root">
      <button
        data-testid="uncle-neo-open-btn"
        onClick={() => setOpen(v => !v)}
        className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/30 backdrop-blur text-white/95 text-xs font-mono tracking-widest transition-colors inline-flex items-center gap-2"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-[#f2b06b] animate-pulse" />
        {open ? "close chat" : "talk to uncle neo"}
      </button>

      {open && (
        <div
          data-testid="uncle-neo-panel"
          className="mt-4 bg-black/60 border border-white/20 backdrop-blur rounded-sm overflow-hidden"
        >
          <div className="px-4 py-2 flex items-center gap-2 border-b border-white/10 text-[10px] font-mono tracking-widest text-white/60">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#f2b06b]" />
            <span>UNCLE NEO · bridged · {sessionId.slice(0, 10)}…</span>
            <span className="flex-1" />
            <span>claude sonnet 4-6 · /app/memory/</span>
          </div>

          <div
            ref={scrollRef}
            data-testid="uncle-neo-transcript"
            className="max-h-[55vh] overflow-y-auto px-4 py-4 space-y-3 font-mono text-sm leading-relaxed"
          >
            {turns.length === 0 && !sending && (
              <div className="text-white/50 text-xs">
                porch light on. say what needs saying.
              </div>
            )}
            {turns.map((t, i) => (
              <div
                key={i}
                data-testid={`uncle-neo-turn-${t.role}-${i}`}
                className={t.role === "user" ? "text-white/90" : "text-[#f2d8a8] whitespace-pre-wrap"}
              >
                <div className="text-[9px] tracking-widest text-white/40 mb-1 flex items-center gap-2">
                  {t.role === "user" ? "paul" : "uncle neo"}
                  {isPaul && t.role === "assistant" && (
                    <button data-testid={`uncle-neo-play-${i}`} onClick={() => say(t.content)}
                            className="text-white/50 hover:text-[#f2b06b] tracking-widest">play ▶</button>
                  )}
                </div>
                {t.content}
              </div>
            ))}
            {sending && (
              <div className="text-[#f2d8a8]/60 text-xs" data-testid="uncle-neo-typing">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#f2b06b] animate-pulse mr-2" />
                uncle neo is thinking…
              </div>
            )}
          </div>

          {err && <div className="px-4 py-2 text-xs font-mono text-yt-red border-t border-white/10">{err}</div>}

          {isPaul && (
            <div className="px-4 pt-3 border-t border-white/10 space-y-2">
              <TalkButton onText={(text) => send(text)} disabled={sending} />
              <button data-testid="uncle-neo-voice-toggle" onClick={toggleVoice}
                      className="text-[10px] font-mono tracking-widest text-white/60 hover:text-white">
                neo speaks every reply: <span className={voiceOn ? "text-[#8fbf7a]" : "text-white/40"}>{voiceOn ? "on" : "off"}</span>
              </button>
            </div>
          )}

          <div className="px-4 py-3 border-t border-white/10 flex gap-2 items-end">
            <textarea
              data-testid="uncle-neo-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="say what needs saying"
              rows={2}
              className="flex-1 bg-black/40 border border-white/20 px-3 py-2 text-white text-sm font-mono resize-none focus:outline-none focus:border-white/50"
            />
            <button
              data-testid="uncle-neo-send-btn"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="px-4 py-3 bg-white/90 text-black text-xs font-mono tracking-widest hover:bg-white disabled:opacity-40"
            >
              {sending ? "…" : "send"}
            </button>
          </div>
          {isPaul && <PhoneLink />}
        </div>
      )}
    </div>
  );
}
